import React from "react";
import useModalEscape from "@/hooks/useModalEscape";

/**
 * BaseModal - Reusable modal component with proper layout
 *
 * Features:
 * - Fixed header and footer, scrollable content
 * - Backdrop click to close
 * - ESC key to close
 * - Prevents content behind from scrolling
 *
 * @param {Object} props
 * @param {React.ReactNode} props.header - Modal header content
 * @param {React.ReactNode} props.children - Modal body content (scrollable)
 * @param {React.ReactNode} props.footer - Modal footer content (optional)
 * @param {Function} props.onClose - Close handler
 * @param {string} props.maxWidth - Max width class (default: "max-w-2xl")
 * @param {boolean} props.closeOnBackdrop - Allow closing on backdrop click (default: true)
 */
const BaseModal = ({
  header,
  children,
  footer,
  onClose,
  maxWidth = "max-w-2xl",
  closeOnBackdrop = true,
}) => {
  useModalEscape(onClose);

  const handleBackdropClick = () => {
    if (closeOnBackdrop && onClose) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl ${maxWidth} w-full mx-4 flex flex-col`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        {header && (
          <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            {header}
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1">{children}</div>

        {/* Footer - Fixed */}
        {footer && (
          <div className="border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseModal;
